// components/categories/CategoryCard.tsx
'use client';

import React, { useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ArrowUpRight, Layers } from 'lucide-react';
import { Category } from '@/data/categories';
import { CornerBracket } from './CornerBracket';

const ACCENT_MAP: Record<string, string> = {
  brass: '#B08D57',
  copper: '#A0522D',
  wood: '#7B5E3A',
  marble: '#8E8E8E',
  aluminium: '#6B7A8C',
  'home-decor': '#8C7A6B',
  furniture: '#5C6B4A',
  lighting: '#C09B3A',
};

const TAG_MAP: Record<string, string> = {
  brass: 'Bestseller',
  copper: 'Premium',
  wood: 'Natural',
  marble: 'Luxury',
  aluminium: 'Industrial',
  'home-decor': 'Curated',
  furniture: 'Handcrafted',
  lighting: 'Trending',
};

interface CategoryCardProps {
  category: Category;
  index: number;
}

export const CategoryCard: React.FC<CategoryCardProps> = ({ category, index }) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const glintRef = useRef<HTMLDivElement | null>(null);

  const quickLift = useRef<gsap.QuickToFunc | null>(null);
  const quickRotX = useRef<gsap.QuickToFunc | null>(null);
  const quickRotY = useRef<gsap.QuickToFunc | null>(null);
  const quickGlint = useRef<gsap.QuickToFunc | null>(null);

  const accent = ACCENT_MAP[category.id] ?? '#B08D57';
  const tag = TAG_MAP[category.id] ?? 'Featured';
  const num = String(index + 1).padStart(2, '0');

  useGSAP(() => {
    if (!cardRef.current) return;

    gsap.set(cardRef.current, {
      transformPerspective: 900,
      transformOrigin: 'center center',
    });

    quickLift.current = gsap.quickTo(cardRef.current, 'y', { duration: 0.55, ease: 'power3.out' });
    quickRotX.current = gsap.quickTo(cardRef.current, 'rotationX', { duration: 0.65, ease: 'power3.out' });
    quickRotY.current = gsap.quickTo(cardRef.current, 'rotationY', { duration: 0.65, ease: 'power3.out' });

    if (glintRef.current) {
      gsap.set(glintRef.current, { x: -220 });
      quickGlint.current = gsap.quickTo(glintRef.current, 'x', {
        duration: 0.6,
        ease: 'power2.out',
      });
    }
  }, { scope: cardRef });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;

    quickRotY.current?.(px * 9);
    quickRotX.current?.(-py * 9);
    quickLift.current?.(-8);
    quickGlint.current?.(
      ((e.clientX - rect.left) / rect.width) * (rect.width + 220) - 220
    );
  };

  const handleMouseLeave = () => {
    quickRotY.current?.(0);
    quickRotX.current?.(0);
    quickLift.current?.(0);
    gsap.to(glintRef.current, { x: -220, duration: 0.4 });
  };

  return (
    /* Outer wrapper — gives space for the outside brackets */
    <div className="relative p-2">

      {/* ── Corner brackets OUTSIDE the card ── */}
      {(['tl', 'tr', 'bl', 'br'] as const).map((c) => (
        <CornerBracket
          key={c}
          corner={c}
          size={18}
          thickness={1.5}
          color={accent}
        />
      ))}

      {/* ── The actual card (tilt target) ── */}
      <div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="relative bg-[#F8F5F0] rounded-[6px] overflow-hidden border border-[#1C1B19]/[0.07] shadow-[0_2px_8px_rgba(28,27,25,0.06)] hover:shadow-[0_28px_56px_-12px_rgba(28,27,25,0.22)] transition-shadow duration-500 will-change-transform cursor-pointer"
      >
        {/* ── Image ── */}
        <Link href={category.href ?? '/products'} className="block">
          <div className="relative w-full aspect-[4/5] overflow-hidden bg-[#EDE9E0]">
            <Image
              src={category.image}
              alt={category.title}
              fill
              sizes="(max-width: 480px) 100vw,(max-width: 768px) 50vw,(max-width: 1280px) 33vw, 25vw"
              className="object-cover transition-transform duration-[1600ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.1]"
              priority={index < 4}
            />

            {/* Glint sweep */}
            <div
              ref={glintRef}
              aria-hidden
              className="absolute inset-y-0 w-[80px] bg-gradient-to-r from-transparent via-white/18 to-transparent -skew-x-12 pointer-events-none z-10"
            />

            {/* Gradient veil */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

            {/* Index — top-left */}
            <div className="absolute top-3 left-3 z-10">
              <span className="font-mono text-[10px] tracking-[0.2em] text-white/60">
                {num}
              </span>
            </div>

            {/* Tag — top-right */}
            <div className="absolute top-3 right-3 z-10">
              <span
                className="inline-block px-2 py-[3px] text-[9px] tracking-[0.14em] uppercase font-semibold rounded-[3px]"
                style={{ background: `${accent}CC`, color: '#FEFDFB' }}
              >
                {tag}
              </span>
            </div>

            {/* Hover reveal panel */}
            <div className="group absolute inset-x-0 bottom-0 translate-y-full hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] z-10">
              <div className="bg-white/10 backdrop-blur-[10px] border-t border-white/15 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Layers size={11} className="text-white/70" strokeWidth={1.5} />
                  <span className="text-[9px] tracking-[0.18em] uppercase text-white/70 font-medium">
                    {category.subtitle}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] tracking-[0.16em] uppercase text-white font-semibold">
                    Explore
                  </span>
                  <ArrowUpRight size={13} className="text-white" strokeWidth={2} />
                </div>
              </div>
            </div>
          </div>

          {/* ── Label ── */}
          <div className="relative bg-[#F8F5F0] px-4 py-[14px] group">
            {/* Bottom colour wipe */}
            <div
              aria-hidden
              className="absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
              style={{ background: `linear-gradient(to right, ${accent}, transparent)` }}
            />

            <span
              className="block text-[9px] tracking-[0.22em] uppercase font-semibold mb-[3px]"
              style={{ color: accent }}
            >
              {category.subtitle}
            </span>

            <div className="flex items-baseline justify-between gap-2">
              <h3 className="text-[15px] font-bold text-[#1C1B19] tracking-tight leading-snug">
                {category.title}
              </h3>
              <ArrowUpRight
                size={14}
                strokeWidth={1.5}
                className="shrink-0 text-[#9A9384]"
              />
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};